import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import * as d3 from 'd3';
import classnames from 'classnames';
import { getValueForAbundance } from 'constants/fcm';
import './NodeAbundanceSlider.scss';

const animationTimer = 2;

const NodeAbundanceSlider = ({
  node,
  abundance,
  cascadeHidden,
  onAbundanceChange,
  fixed,
  tempOverride,
  noAnimate,
}) => {
  // This is more stable than abundance because of cascading logic
  const actualAbundance = useMemo(() => {
    return node.fixed ? Number(node.abundance) : Number(node.abundanceResult);
  }, [node]);

  const currentValue = useMemo(() => {
    return Number(cascadeHidden ? node.oldAbundance : abundance);
  }, [node, abundance, cascadeHidden]);

  const [animationIsRunning, setAnimationIsRunning] = useState(1);

  // Initial node value
  const initialValue = useRef(actualAbundance);
  // Active value of changing the setting range
  const [tempValue, setTempValue] = useState(actualAbundance);
  // Gets set by incoming node's abundance only when user set
  const [userSetValue, setUserSetValue] = useState(null);
  // Sets when automatically set
  const [autoValue, setAutoValue] = useState(actualAbundance);
  // Last value
  const [lastValue, setLastValue] = useState(actualAbundance);

  useEffect(() => {
    if (animationIsRunning === 0) {
      if (noAnimate) {
        setAnimationIsRunning(1);
      } else {
        const fullWidth = Math.abs(lastValue - currentValue);
        const baseSpeed = fullWidth * animationTimer;
        const int = fullWidth * 100;
        const fullTime = baseSpeed * 1000;
        let div = fullTime / int;
        if (isNaN(div)) div = 1;

        const interval = setInterval(() => {
          setAnimationIsRunning(n => n + 1 / div);
        }, int);

        setTimeout(() => {
          clearInterval(interval);
          setAnimationIsRunning(1);
        }, fullTime);
      }
    }
  }, [animationIsRunning, lastValue, currentValue, noAnimate]);

  useEffect(() => {
    // autoValue only affects non-fixed!!!
    const prev = fixed ? userSetValue : autoValue;
    if (prev !== actualAbundance) {
      setAnimationIsRunning(0);
      setLastValue(prev);
    }
  }, [fixed, autoValue, userSetValue, actualAbundance]);

  // Get the node abundance value
  const nodeAbundance = getValueForAbundance(node, tempValue);

  const onChange = useCallback(
    value => {
      setTempValue(Number(value));
      onAbundanceChange(Number(value));
    },
    [onAbundanceChange]
  );

  // Pre-run value CSS
  const tempValueCss = useMemo(() => {
    const lesserVal = Math.min(tempValue, initialValue.current);
    const greaterVal = Math.max(tempValue, initialValue.current);

    const marginLeft = `${lesserVal * 100}%`;
    const widthPercent = (greaterVal - lesserVal) * 100;
    const width = `calc(${widthPercent}%)`;

    return {
      marginLeft,
      width,
    };
  }, [tempValue]);

  // Results bar CSS
  const resultsCssPositive = useMemo(() => {
    const lesserVal = initialValue.current;
    const greaterVal = Math.max(currentValue, initialValue.current);

    const widthPercent = (greaterVal - lesserVal) * 100;

    const marginLeft = `${lesserVal * 100}%`;
    const width = `calc(${widthPercent}%)`;

    const fullWidth = Math.abs(lastValue - currentValue);
    const baseSpeed = fullWidth * animationTimer;

    let transitionSpeed = baseSpeed;
    let delaySpeed = 0;

    // If starting negative and going positive
    if (
      lastValue < initialValue.current &&
      currentValue > initialValue.current
    ) {
      const pos = (currentValue - initialValue.current) / fullWidth;
      const neg = (initialValue.current - lastValue) / fullWidth;
      transitionSpeed = Math.abs(baseSpeed * pos);
      delaySpeed = Math.abs(baseSpeed * neg);
    }

    // If starting positive and going negative
    if (
      lastValue > initialValue.current &&
      currentValue < initialValue.current
    ) {
      const pos = (lastValue - initialValue.current) / fullWidth;
      transitionSpeed = Math.abs(baseSpeed * pos);
    }

    const ignoreFixed = !fixed ? false : tempValue !== actualAbundance;

    if (ignoreFixed) {
      return {
        marginLeft,
        width,
        transition: 'none',
      };
    }

    if (currentValue < initialValue.current) {
      return {
        marginLeft,
        left: 0,
        width: 0,
        ...(!noAnimate && {
          transition: `width ${transitionSpeed}s linear ${delaySpeed}s`,
        }),
      };
    }

    return {
      marginLeft,
      width,
      ...(!noAnimate && {
        transition: `width ${transitionSpeed}s linear ${delaySpeed}s`,
      }),
    };
  }, [currentValue, lastValue, actualAbundance, tempValue, fixed, noAnimate]);

  const resultsCssNegative = useMemo(() => {
    const lesserVal = Math.min(currentValue, initialValue.current);
    const greaterVal = initialValue.current;

    const widthPercent = (greaterVal - lesserVal) * 100;

    const marginLeft = `${lesserVal * 100 + widthPercent}%`;
    const width = `${widthPercent}%`;

    const fullWidth = Math.abs(lastValue - currentValue);
    const baseSpeed = fullWidth * animationTimer;
    let transitionSpeed = baseSpeed;
    let delaySpeed = 0;

    // If starting positive and going negative
    if (
      lastValue > initialValue.current &&
      currentValue < initialValue.current
    ) {
      const pos = (lastValue - initialValue.current) / fullWidth;
      const neg = (initialValue.current - currentValue) / fullWidth;
      delaySpeed = Math.abs(baseSpeed * pos);
      transitionSpeed = Math.abs(baseSpeed * neg);
    }
    // If starting negative and going positive
    if (
      lastValue < initialValue.current &&
      currentValue > initialValue.current
    ) {
      const neg = (initialValue.current - lastValue) / fullWidth;
      transitionSpeed = Math.abs(baseSpeed * neg);
    }

    const ignoreFixed = !fixed ? false : tempValue !== actualAbundance;

    if (ignoreFixed) {
      return {
        marginLeft,
        width,
        left: `${widthPercent * -1}%`,
        transition: 'none',
      };
    }

    if (currentValue > initialValue.current) {
      return {
        marginLeft,
        left: 0,
        width: 0,
        ...(!noAnimate && {
          transition: `left ${transitionSpeed}s linear ${delaySpeed}s, width ${transitionSpeed}s linear ${delaySpeed}s`,
        }),
      };
    }

    return {
      marginLeft,
      left: `${widthPercent * -1}%`,
      width,
      ...(!noAnimate && {
        transition: `left ${transitionSpeed}s linear ${delaySpeed}s, width ${transitionSpeed}s linear ${delaySpeed}s`,
      }),
    };
  }, [currentValue, lastValue, fixed, tempValue, actualAbundance, noAnimate]);

  // Slider bubble CSS
  const bubbleCssFixed = useMemo(() => {
    let bubbleWidth = 36;

    const rangeWidth = document.getElementById(`${node.id}-range`)?.offsetWidth;

    let marginLeft = `calc(${tempValue * 100}% - ${bubbleWidth / 2}px`;

    if (rangeWidth - bubbleWidth / 2 <= rangeWidth * tempValue) {
      marginLeft = `${rangeWidth - bubbleWidth}px`;
    }

    if (bubbleWidth / 2 >= rangeWidth * tempValue) {
      marginLeft = '0px';
    }

    return {
      marginLeft,
    };
  }, [node, tempValue]);

  const bubbleCssUnfixed = useMemo(() => {
    let bubbleWidth = 36;

    const rangeWidth = document.getElementById(`${node.id}-range`)?.offsetWidth;

    let marginLeft = `calc(${currentValue * 100}% - ${bubbleWidth / 2}px`;

    if (rangeWidth - bubbleWidth / 2 <= rangeWidth * currentValue) {
      marginLeft = `${rangeWidth - bubbleWidth}px`;
    }

    if (bubbleWidth / 2 >= rangeWidth * currentValue) {
      marginLeft = '0px';
    }

    const fullWidth = Math.abs(lastValue - currentValue);
    const baseSpeed = fullWidth * animationTimer;

    return {
      marginLeft,
      ...(!noAnimate && { transition: `all ${baseSpeed}s linear` }),
    };
  }, [node, currentValue, lastValue, noAnimate]);

  // Slider handle CSS
  const sliderHandleCssFixed = useMemo(() => {
    const marginLeft = `calc(${tempValue * 100}% - ${2 * tempValue}px`;

    const fullWidth = Math.abs(lastValue - currentValue);
    const baseSpeed = fullWidth * animationTimer;

    return {
      marginLeft,
      ...(tempValue === currentValue && {
        transition: `all ${baseSpeed}s linear`,
      }),
    };
  }, [currentValue, tempValue, lastValue]);

  const sliderHandleCssUnfixed = useMemo(() => {
    const marginLeft = `calc(${currentValue * 100}% - ${2 * currentValue}px`;

    const fullWidth = Math.abs(lastValue - currentValue);
    const baseSpeed = fullWidth * animationTimer;

    return {
      marginLeft,
      ...(!noAnimate && { transition: `all ${baseSpeed}s linear` }),
    };
  }, [currentValue, lastValue, noAnimate]);

  // True when nodes receive new values after NOT being changed (reactionary)
  const isReceivingValue = useMemo(() => {
    return actualAbundance !== autoValue && tempValue === autoValue;
  }, [tempValue, actualAbundance, autoValue]);

  // True when resetting (resets those that user set)
  const isResettingValue = useMemo(() => {
    return !tempOverride;
  }, [tempOverride]);

  // Because of cascading, we need to use actual node.abundance rather
  // than abundance to set a stable userSetValue to check
  useEffect(() => {
    if (actualAbundance !== userSetValue && actualAbundance === tempValue) {
      setUserSetValue(actualAbundance);
    }
  }, [actualAbundance, tempValue, userSetValue]);

  useEffect(() => {
    if (isReceivingValue) {
      setTempValue(actualAbundance);
      setAutoValue(actualAbundance);
    }

    if (isResettingValue) {
      setTempValue(actualAbundance);
      setUserSetValue(actualAbundance);
      setAutoValue(actualAbundance);
    }
  }, [actualAbundance, isResettingValue, isReceivingValue]);

  const displayAbundance = useMemo(() => {
    const lastAbundance = getValueForAbundance(node, lastValue);
    const nextAbundance = getValueForAbundance(node, actualAbundance);
    const scale = d3.scaleLinear([0, 1], [lastAbundance, nextAbundance]);
    const decimalPlaces = `${nextAbundance}`.split('.')?.[1]?.length ?? 0;
    // Clamp to nextAbundance once animation reaches or passes 1 so the bubble
    // always settles to the correct final value rather than an intermediate one.
    return (animationIsRunning >= 1
      ? nextAbundance
      : scale(animationIsRunning)
    ).toFixed(decimalPlaces);
  }, [node, actualAbundance, lastValue, animationIsRunning]);

  return (
    <>
      <div className="NodeAbundanceSlider">
        <div id={`${node.id}-range`} className="NodeAbundanceSlider-range">
          <div
            id={`${node.id}-bubble`}
            className={classnames('NodeAbundanceSlider-bubble', {
              hide: fixed,
            })}
            style={fixed ? bubbleCssFixed : bubbleCssUnfixed}
          >
            {fixed ? nodeAbundance : displayAbundance}
          </div>
          <input
            className="NodeAbundanceSlider-slider"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={tempValue}
            onChange={e => onChange(e.target.value)}
          />
          <div
            className={classnames('NodeAbundanceSlider-results-value positive')}
            style={resultsCssPositive}
          />
          <div
            className={classnames('NodeAbundanceSlider-results-value negative')}
            style={resultsCssNegative}
          />

          {fixed && tempValue !== currentValue ? (
            <div
              className="NodeAbundanceSlider-temp-value"
              style={tempValueCss}
            />
          ) : null}
          <div
            className="NodeAbundanceSlider-slider-handle interactiveTourGettingStarted-6"
            style={fixed ? sliderHandleCssFixed : sliderHandleCssUnfixed}
          />
        </div>
      </div>
    </>
  );
};

export default NodeAbundanceSlider;
